import {
  Box,
  Divider,
  Table,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { PopupStyles } from "./CustomerDetailsStyles";
import Theme from "../../../../utils/Theme";

// Fallback labels if artifact questions aren't loaded yet
const FALLBACK_VEHICLE_Q     = "What type of vehicle is it?";
const FALLBACK_TRANSACTION_Q = "What type of transaction was it?";
const FALLBACK_YMM_Q         = "What vehicle did you purchase today?";

// Map single-char keys → human-readable labels (mirrors VehicleTransactionResponse.jsx)
const VEHICLE_TYPE_MAP = {
  N:   "New",
  U:   "Used",
  CPO: "Certified Pre-Owned",
  O:   "Other",
};
const TRANSACTION_TYPE_MAP = {
  F: "Finance",
  L: "Lease",
  C: "Cash",
  B: "Balloon",
};

const ArtifactService = ({ artifactService, vehicleArtifact }) => {
  const formatTime = (secs) => {
    const hours   = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    const pad     = (u) => u.toString().padStart(2, "0");
    return `${pad(hours)}.${pad(minutes)}.${pad(seconds)}`;
  };

  const vtr = artifactService?.vehicleTransactionResponse;
  const smr = artifactService?.serviceMaintenanceResponse;

  // Use real artifact question text; fall back to defaults if not loaded
  const vehicleQuestion     = vehicleArtifact?.vehicle_message     || FALLBACK_VEHICLE_Q;
  const transactionQuestion = vehicleArtifact?.transaction_message || FALLBACK_TRANSACTION_Q;
  const ymmQuestion         = FALLBACK_YMM_Q;

  // Only treat vehicle transaction data as "present" if it actually has
  // a vehicle_type or transaction_type value (avoids showing an empty
  // "Vehicle Details" heading when vtr exists but both fields are blank)
  const hasVehicleDetails = Boolean(vtr?.vehicle_type || vtr?.transaction_type);

  return (
    <Box>
      {artifactService?.surveyResponse?.length > 0 ||
      artifactService?.reviewResponse?.length > 0 ||
      artifactService?.referralResponse?.length > 0 ||
      // artifactService?.appointmentResponse?.length > 0 ||
      hasVehicleDetails ||
      smr?.appointment_set ||
      artifactService?.videoResponse?.length > 0 ? (
        <>
          {/* Survey Details */}
          {artifactService?.surveyResponse?.length > 0 && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Survey Details:
                </span>
                {artifactService.surveyResponse.map((survey, index) => (
                  <TableRow sx={PopupStyles.rowStyle} key={index}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>{survey.question}</span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {survey.answer ? survey.answer : "N/A"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Review Details */}
          {artifactService?.reviewResponse?.length > 0 && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Review Details:
                </span>
                {artifactService.reviewResponse.map((review, index) => (
                  <TableRow sx={PopupStyles.rowStyle} key={index}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>{review.review_question}</span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {review.answer ? review.answer : "N/A"}
                        </span>
                        {review.second_review_question && (
                          <div
                            style={{
                              display:       "flex",
                              flexDirection: "column",
                              gap:           "4px",
                              marginTop:     "8px",
                            }}
                          >
                            <span>{review.second_review_question}</span>
                            <span style={{ color: Theme.palette.primary.main }}>
                              {review.second_review_answer
                                ? review.second_review_answer
                                : "N/A"}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Referral Details */}
          {artifactService?.referralResponse?.length > 0 && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Referral Details:
                </span>
                {artifactService.referralResponse.map((referral, index) => (
                  <TableRow sx={PopupStyles.rowStyle} key={index}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>Name: {referral.name}</span>
                        <span>
                          Mobile Number:{" "}
                          {referral.mobile_number ? referral.mobile_number : "N/A"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Appointment Details */}
          {/* {artifactService?.appointmentResponse?.length > 0 && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Appointment Details:
                </span>
                <TableRow sx={PopupStyles.rowStyle}>
                  <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span>
                        Appointment Notification:{" "}
                        <span style={{ color: Theme.palette.primary.main }}>
                          {artifactService?.appointment_preference === 1
                            ? "Email"
                            : artifactService?.appointment_preference === 2
                              ? "Text"
                              : "None"}
                        </span>
                      </span>
                      {artifactService.appointmentResponse.map((appointment, index) => (
                        <div
                          key={index}
                          style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                        >
                          <span>
                            Date & Time:{" "}
                            <span style={{ color: Theme.palette.primary.main }}>
                              {`${appointment.date} ${appointment?.time}`}
                            </span>
                          </span>
                          <span>
                            Preference:{" "}
                            {appointment.preference ? appointment.preference : "N/A"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )} */}

          {/* Vehicle Details — Q&A format, questions from artifact config */}
          {hasVehicleDetails && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Vehicle Details:
                </span>

                {/* Q1 — Vehicle Type: question text from artifact.vehicle_message */}
                {vtr.vehicle_type && (
                  <TableRow sx={PopupStyles.rowStyle}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>{vehicleQuestion}</span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {VEHICLE_TYPE_MAP[vtr.vehicle_type] ?? vtr.vehicle_type}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Q2 — Transaction Type: question text from artifact.transaction_message */}
                {vtr.transaction_type && (
                  <TableRow sx={PopupStyles.rowStyle}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>{transactionQuestion}</span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {TRANSACTION_TYPE_MAP[vtr.transaction_type] ?? vtr.transaction_type}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Q3 — Year / Make / Model */}
                {/* {(vtr.year || vtr.make || vtr.model) && (
                  <TableRow sx={PopupStyles.rowStyle}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span>{ymmQuestion}</span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {[vtr.year, vtr.make, vtr.model].filter(Boolean).join(" ")}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )} */}
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Service Maintenance Details */}
     {/* {smr?.appointment_set && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Service Maintenance Details:
                </span>
                <TableRow sx={PopupStyles.rowStyle}>
                  <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span>
                        Date:{" "}
                        <span style={{ color: Theme.palette.primary.main }}>
                          {smr.appointment_date || "N/A"}
                        </span>
                      </span>
                      <span>
                        Time:{" "}
                        <span style={{ color: Theme.palette.primary.main }}>
                          {smr.appointment_time || "N/A"}
                        </span>
                      </span>
                      <span>
                        Preference:{" "}
                        <span style={{ color: Theme.palette.primary.main }}>
                          {smr.appointment_preference || "N/A"}
                        </span>
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )} */}

          {/* Video Details */}
          {artifactService?.videoResponse?.length > 0 && (
            <>
              <Table>
                <span style={{ fontWeight: "bold", fontSize: 14 }}>
                  Video Details:
                </span>
                {artifactService.videoResponse.map((video, index) => (
                  <TableRow sx={PopupStyles.rowStyle} key={index}>
                    <TableCell className="p-6" colSpan={3} sx={PopupStyles.childCell}>
                      <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                        <span>{video.title}: </span>
                        <span style={{ color: Theme.palette.primary.main }}>
                          {video.watched_secs ? formatTime(video.watched_secs) : "N/A"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
              <Divider sx={{ mb: 2 }} />
            </>
          )}
        </>
      ) : (
        <Box
          sx={{
            display:        "flex",
            justifyContent: "center",
            alignItems:     "center",
            height:         "100%",
          }}
        >
          <Typography>No Data Available</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ArtifactService;